import { api } from ".";
import {
  IClaim,
  IClaimResponse,
  IClaimFilters,
  IAIAnalysisResult,
  IDifferenceAnalysisResult,
  IUnderpaymentAnalysisPrompt,
  ISimilarClaimsResponse,
  ResolutionStatus,
  IBulkClaimUpdateResponse,
  IBulkAppealGroup,
  IBulkAppealLetter,
  IAppealTemplate,
  IReprocessedClaimsFilters,
  IReprocessedClaimsFilterOptions,
  IReprocessedClaimsResponse,
  IReprocessedClaimsRescanResponse,
} from "../../types/claim";
import {
  bulkAppealFilterParams,
  IBulkAppealFilters,
} from "./bulkAppealFilterParams";
import { reprocessedClaimsParams } from "./reprocessedClaimsParams";

/**
 * RTK Query API slice for claims endpoints
 * Provides hooks for fetching claim data with filtering, sorting, and pagination
 */
const claimQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getClaims: builder.query<IClaimResponse, IClaimFilters>({
      query: (filters) => {
        // Build query parameters
        const params: Record<string, string> = {
          accountId: filters.accountId,
        };

        if (filters.search) {
          params.search = filters.search;
        }

        if (filters.startDate) {
          [params.startDate] = filters.startDate.toISOString().split("T");
        }

        if (filters.endDate) {
          [params.endDate] = filters.endDate.toISOString().split("T");
        }

        if (filters.sortField) {
          params.sortField = filters.sortField;
        }

        if (filters.sortOrder) {
          params.sortOrder = filters.sortOrder;
        }

        if (filters.page) {
          params.page = filters.page.toString();
        }

        if (filters.pageSize) {
          params.pageSize = filters.pageSize.toString();
        }

        if (filters.chargeBelowContractRateFilter) {
          params.chargeBelowContractRateFilter =
            filters.chargeBelowContractRateFilter;
        }

        if (filters.effectiveYearMissingFilter) {
          params.effectiveYearMissingFilter =
            filters.effectiveYearMissingFilter;
        }

        if (filters.minDifferencePercent !== undefined) {
          params.minDifferencePercent = filters.minDifferencePercent.toString();
        }

        if (filters.maxDifferencePercent !== undefined) {
          params.maxDifferencePercent = filters.maxDifferencePercent.toString();
        }

        if (filters.minDifferenceAmount !== undefined) {
          params.minDifferenceAmount = filters.minDifferenceAmount.toString();
        }

        if (filters.categories && filters.categories.length > 0) {
          params.categories = filters.categories.join(",");
        }

        if (filters.underpaymentOnly !== undefined) {
          params.underpaymentOnly = filters.underpaymentOnly.toString();
        }

        if (filters.paymentTypes && filters.paymentTypes.length > 0) {
          params.paymentTypes = filters.paymentTypes.join(",");
        }

        if (filters.denialStatuses && filters.denialStatuses.length > 0) {
          params.denialStatuses = filters.denialStatuses.join(",");
        }

        if (
          filters.denialClassificationCodes &&
          filters.denialClassificationCodes.length > 0
        ) {
          params.denialClassificationCodes =
            filters.denialClassificationCodes.join(",");
        }

        if (
          filters.renderingTaxonomies &&
          filters.renderingTaxonomies.length > 0
        ) {
          params.renderingTaxonomies = filters.renderingTaxonomies.join(",");
        }

        if (filters.resolutionFilter) {
          params.resolutionFilter = filters.resolutionFilter;
        }

        if (filters.classificationFilter) {
          params.classificationFilter = filters.classificationFilter;
        }

        if (filters.payerIdFilter) {
          params.payerIdFilter = filters.payerIdFilter;
        }

        if (filters.payerIdFilters && filters.payerIdFilters.length > 0) {
          params.payerIdFilters = filters.payerIdFilters.join(",");
        }

        if (filters.patientClassFilter) {
          params.patientClassFilter = filters.patientClassFilter;
        }

        if (filters.claimCategoryFilter) {
          params.claimCategoryFilter = filters.claimCategoryFilter;
        }

        if (filters.hasMatchedOrder) {
          params.hasMatchedOrder = "true";
        }

        if (filters.minEventCount !== undefined) {
          params.minEventCount = String(filters.minEventCount);
        }

        if (filters.maxEventCount !== undefined) {
          params.maxEventCount = String(filters.maxEventCount);
        }

        if (
          filters.ediConversionFileIds &&
          filters.ediConversionFileIds.length > 0
        ) {
          params.ediConversionFileIds = filters.ediConversionFileIds.join(",");
        }

        return {
          url: "/claims",
          params,
        };
      },
      transformResponse: (response: any): IClaimResponse => {
        // Transform ISO date strings to Date objects
        const data: IClaim[] = response.data.map((item: any) => ({
          ...item,
          patientDOB: item.patientDOB ? new Date(item.patientDOB) : null,
          serviceDate: new Date(item.serviceDate),
          ediConversionFile: item.ediConversionFile
            ? {
                ...item.ediConversionFile,
                createdAt: new Date(item.ediConversionFile.createdAt),
              }
            : undefined,
        }));

        return {
          data,
          totalCount: response.totalCount,
          page: response.page,
          pageSize: response.pageSize,
          message: response.message,
        };
      },
      providesTags: ["Claims"],
    }),

    exportClaims: builder.query<
      { downloadUrl: string },
      Omit<IClaimFilters, "page" | "pageSize">
    >({
      query: (filters) => {
        // Build query parameters (same as getClaims but without pagination)
        const params: Record<string, string> = {
          accountId: filters.accountId,
        };

        if (filters.search) {
          params.search = filters.search;
        }

        if (filters.startDate) {
          [params.startDate] = filters.startDate.toISOString().split("T");
        }

        if (filters.endDate) {
          [params.endDate] = filters.endDate.toISOString().split("T");
        }

        if (filters.sortField) {
          params.sortField = filters.sortField;
        }

        if (filters.sortOrder) {
          params.sortOrder = filters.sortOrder;
        }

        if (filters.chargeBelowContractRateFilter) {
          params.chargeBelowContractRateFilter =
            filters.chargeBelowContractRateFilter;
        }

        if (filters.effectiveYearMissingFilter) {
          params.effectiveYearMissingFilter =
            filters.effectiveYearMissingFilter;
        }

        if (filters.minDifferencePercent !== undefined) {
          params.minDifferencePercent = filters.minDifferencePercent.toString();
        }

        if (filters.maxDifferencePercent !== undefined) {
          params.maxDifferencePercent = filters.maxDifferencePercent.toString();
        }

        if (filters.minDifferenceAmount !== undefined) {
          params.minDifferenceAmount = filters.minDifferenceAmount.toString();
        }

        if (filters.categories && filters.categories.length > 0) {
          params.categories = filters.categories.join(",");
        }

        if (filters.underpaymentOnly !== undefined) {
          params.underpaymentOnly = filters.underpaymentOnly.toString();
        }

        if (filters.paymentTypes && filters.paymentTypes.length > 0) {
          params.paymentTypes = filters.paymentTypes.join(",");
        }

        if (filters.denialStatuses && filters.denialStatuses.length > 0) {
          params.denialStatuses = filters.denialStatuses.join(",");
        }

        if (
          filters.denialClassificationCodes &&
          filters.denialClassificationCodes.length > 0
        ) {
          params.denialClassificationCodes =
            filters.denialClassificationCodes.join(",");
        }

        if (
          filters.renderingTaxonomies &&
          filters.renderingTaxonomies.length > 0
        ) {
          params.renderingTaxonomies = filters.renderingTaxonomies.join(",");
        }

        if (filters.resolutionFilter) {
          params.resolutionFilter = filters.resolutionFilter;
        }

        if (filters.classificationFilter) {
          params.classificationFilter = filters.classificationFilter;
        }

        if (filters.payerIdFilter) {
          params.payerIdFilter = filters.payerIdFilter;
        }

        if (filters.payerIdFilters && filters.payerIdFilters.length > 0) {
          params.payerIdFilters = filters.payerIdFilters.join(",");
        }

        if (filters.patientClassFilter) {
          params.patientClassFilter = filters.patientClassFilter;
        }

        if (filters.claimCategoryFilter) {
          params.claimCategoryFilter = filters.claimCategoryFilter;
        }

        if (filters.hasMatchedOrder) {
          params.hasMatchedOrder = "true";
        }

        if (filters.minEventCount !== undefined) {
          params.minEventCount = String(filters.minEventCount);
        }

        if (filters.maxEventCount !== undefined) {
          params.maxEventCount = String(filters.maxEventCount);
        }

        if (
          filters.ediConversionFileIds &&
          filters.ediConversionFileIds.length > 0
        ) {
          params.ediConversionFileIds = filters.ediConversionFileIds.join(",");
        }

        return {
          url: "/claims/export",
          params,
        };
      },
    }),

    analyzeClaim: builder.mutation<
      { analysis: IAIAnalysisResult },
      { claimId: string; regenerate?: boolean }
    >({
      query: ({ claimId, regenerate = false }) => ({
        url: `/claims/${claimId}/analyze`,
        method: "POST",
        body: { regenerate },
      }),
      // Invalidate claims cache to trigger refetch with new aiAnalysisResult
      invalidatesTags: ["Claims"],
    }),

    /**
     * Analyze differences between underpaid claim and similar correctly-paid claims
     * Returns plain text AI analysis
     */
    analyzeDifferences: builder.mutation<
      IDifferenceAnalysisResult,
      { claimId: string; regenerate?: boolean }
    >({
      query: ({ claimId, regenerate = false }) => ({
        url: `/claims/${claimId}/analyze-differences`,
        method: "POST",
        body: { regenerate },
      }),
    }),

    /**
     * Update resolution status
     * Updates the resolution status of a claim with audit trail
     */
    updateResolutionStatus: builder.mutation<
      IClaim,
      { claimId: string; status: ResolutionStatus; notes?: string }
    >({
      query: ({ claimId, status, notes }) => ({
        url: `/claims/${claimId}/resolution-status`,
        method: "PATCH",
        body: { status, notes },
      }),
      // Invalidate claims cache to trigger refetch with updated status
      invalidatesTags: ["Claims"],
    }),

    /**
     * Get active prompt version
     * Retrieves the currently active AI prompt version (most recent)
     * SuperAdmin-only endpoint
     * Note: Endpoint path stays as /underpayment-analysis-prompts (table not renamed)
     */
    getActivePrompt: builder.query<IUnderpaymentAnalysisPrompt, void>({
      query: () => ({
        url: "/underpayment-analysis-prompts/active",
      }),
      providesTags: ["PromptVersions"],
    }),

    /**
     * Create new prompt version
     * Creates a new prompt version with auto-incremented version number
     * SuperAdmin-only endpoint
     * Note: Endpoint path stays as /underpayment-analysis-prompts (table not renamed)
     */
    createPromptVersion: builder.mutation<
      IUnderpaymentAnalysisPrompt,
      {
        identity: string;
        issueSummary: string;
        classification: string;
        appealLetter: string;
        reprocessLetter: string;
        finalRules: string;
      }
    >({
      query: (data) => ({
        url: "/underpayment-analysis-prompts",
        method: "POST",
        body: data,
      }),
      // Invalidate prompt versions cache to trigger refetch
      invalidatesTags: ["PromptVersions"],
    }),

    /**
     * Get all prompt versions
     * Retrieves all prompt versions for viewing history
     * SuperAdmin-only endpoint
     * Note: Endpoint path stays as /underpayment-analysis-prompts (table not renamed)
     */
    getAllPromptVersions: builder.query<IUnderpaymentAnalysisPrompt[], void>({
      query: () => ({
        url: "/underpayment-analysis-prompts",
      }),
      providesTags: ["PromptVersions"],
    }),

    /**
     * Get distinct AI classifications
     * Retrieves unique AI classification codes from analyzed claims for an account
     * Used to populate the classification filter dropdown dynamically
     */
    getDistinctClassifications: builder.query<
      { code: string; title: string }[],
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/classifications",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * CLE-2565: Get distinct deterministic denial classification codes present on an account.
     * Used to populate the Denial Classification filter with only the codes that actually occur
     * for this account (instead of the full static taxonomy).
     */
    getDistinctDenialClassifications: builder.query<
      { code: string; title: string }[],
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/denial-classifications",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Get distinct Clear payers
     * Retrieves unique Clear payers (id + current name) from claims for an account
     * Used to populate the Clear Payer filter dropdown
     */
    getDistinctClearPayers: builder.query<
      Array<{
        id: number | null;
        name: string;
        parentPayerName: string | null;
      }>,
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/clear-payers",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Get distinct EDI conversion files (CLE-2491)
     * Retrieves the EDI files that produced claims for an account, used to populate the
     * "EDI file" filter dropdown in the claims table.
     */
    getDistinctEdiFiles: builder.query<
      Array<{
        id: string;
        displayName: string;
        fileName: string;
        createdAt: string;
        claimCount: number;
      }>,
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/edi-files",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Get similar correctly-paid claims
     * Finds claims from the same payer with overlapping "problem" CPT codes that were paid correctly
     * Requires AI analysis with underpaid line items
     * Used as evidence for appeals
     */
    getSimilarCorrectClaims: builder.query<
      ISimilarClaimsResponse,
      { claimId: string }
    >({
      query: ({ claimId }) => ({
        url: `/claims/${claimId}/similar-correct`,
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Reprocess a single claim from its original EDI file
     * Re-parses with current payer mappings and fee schedules
     */
    reprocessClaim: builder.mutation<{ data: IClaim; message: string }, string>(
      {
        query: (claimId) => ({
          url: `/claims/${claimId}/reprocess`,
          method: "POST",
        }),
        invalidatesTags: ["Claims", "PrecalculatedAllowables"],
      }
    ),

    /**
     * Get a single claim by ID
     * Retrieves full claim details including line items
     * Used for navigation between claims in the modal
     */
    getClaimById: builder.query<IClaim, { claimId: string }>({
      query: ({ claimId }) => ({
        url: `/claims/${claimId}`,
      }),
      transformResponse: (response: any): IClaim => {
        // Transform ISO date strings to Date objects
        return {
          ...response,
          patientDOB: response.patientDOB
            ? new Date(response.patientDOB)
            : null,
          serviceDate: new Date(response.serviceDate),
          ediConversionFile: response.ediConversionFile
            ? {
                ...response.ediConversionFile,
                createdAt: new Date(response.ediConversionFile.createdAt),
              }
            : undefined,
        };
      },
      providesTags: ["Claims"],
    }),

    /**
     * Check if NCCI bypass is available for a claim
     * Returns whether the claim has an NCCI edit that can be bypassed
     */
    checkNcciBypassAvailable: builder.query<
      { data: { ncciBypassAvailable: boolean }; message: string },
      { claimId: string }
    >({
      query: ({ claimId }) => ({
        url: `/claims/${claimId}/ncci-bypass/available`,
      }),
      providesTags: ["Claims"],
    }),

    /**
     * Toggle NCCI bypass for a claim
     * Enables or disables NCCI bypass and returns updated claim data
     */
    toggleNcciBypass: builder.mutation<
      {
        data: {
          claim: IClaim;
          /** Full CPT / adjustment tree for Expected amounts (same role as estimate pricing breakdown). */
          calculatedData?: unknown;
          bypassAmountInCents: number;
          /** Signed delta: newExpectedAmount − previousExpectedAmount */
          bypassedCodes: string[];
          previousExpectedAmount?: number;
          newExpectedAmount?: number;
          newAmountDifference?: number;
          newPaymentType?: string;
        };
        message: string;
      },
      { claimId: string; enabled: boolean }
    >({
      query: ({ claimId, enabled }) => ({
        url: `/claims/${claimId}/ncci-bypass`,
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: ["Claims"],
    }),

    /**
     * Change the matched payer on a claim and reprocess
     * Returns the reprocessed claim with updated pricing
     */
    changeClaimPayer: builder.mutation<
      { data: IClaim; message: string },
      { claimId: string; payerId: number | null }
    >({
      query: ({ claimId, payerId }) => ({
        url: `/claims/${claimId}/payer`,
        method: "PATCH",
        body: { payerId },
      }),
      invalidatesTags: ["Claims", "PrecalculatedAllowables"],
    }),

    /**
     * CLE-2500: change the mapped provider on a claim and reprocess.
     * Returns the reprocessed claim with updated pricing.
     */
    changeClaimProvider: builder.mutation<
      { data: IClaim; message: string },
      { claimId: string; providerId: string }
    >({
      query: ({ claimId, providerId }) => ({
        url: `/claims/${claimId}/provider`,
        method: "PATCH",
        body: { providerId },
      }),
      invalidatesTags: ["Claims", "PrecalculatedAllowables"],
    }),

    /**
     * CLE-2172: bulk change matched payer across up to 25 claims.
     * Returns 200 even on partial failure — see IBulkClaimUpdateResponse.
     */
    bulkChangeClaimPayer: builder.mutation<
      IBulkClaimUpdateResponse,
      { claimIds: string[]; payerId: number | null }
    >({
      query: (body) => ({
        url: `/claims/bulk/payer`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Claims", "PrecalculatedAllowables"],
    }),

    /**
     * CLE-2172: bulk update resolution status across up to 25 claims.
     * Transactional on the server — all updates apply or none do.
     */
    bulkUpdateResolutionStatus: builder.mutation<
      IBulkClaimUpdateResponse,
      { claimIds: string[]; status: ResolutionStatus; notes?: string }
    >({
      query: (body) => ({
        url: `/claims/bulk/resolution-status`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Claims"],
    }),

    /**
     * CLE-2172: bulk reprocess up to 25 claims from their original EDI.
     * Per-claim try/catch — partial failures returned in the 200 body.
     */
    bulkReprocessClaim: builder.mutation<
      IBulkClaimUpdateResponse,
      { claimIds: string[] }
    >({
      query: (body) => ({
        url: `/claims/bulk/reprocess`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Claims", "PrecalculatedAllowables"],
    }),

    /**
     * CLE-2551: run AI analysis on up to 25 selected claims. Responds 202 —
     * the analyses finish in the background, so `claimCount` is what was
     * queued, not what has completed.
     */
    bulkAnalyzeClaims: builder.mutation<
      { claimCount: number },
      { claimIds: string[] }
    >({
      query: (body) => ({
        url: `/claims/bulk/analyze`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Claims"],
    }),

    /**
     * CLE-2702: bulk-appeal opportunities — underpayments grouped by payer ×
     * denial classification for the Bulk Appeals tab. Any user with access to
     * the account (SuperAdmin-only until CLE-2892).
     */
    getBulkAppealOpportunities: builder.query<
      IBulkAppealGroup[],
      { accountId: string; filters?: IBulkAppealFilters }
    >({
      query: ({ accountId, filters }) => ({
        url: `/claims/bulk-appeal-opportunities`,
        // CLE-2822: optional service-date range + rendering taxonomies.
        params: { accountId, ...bulkAppealFilterParams(filters) },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * CLE-2702: assembled bulk-appeal letter (template narrative + claim table +
     * totals) for one (classification × payer) group. Fetched on demand when a
     * row is opened. Any user with access to the account (SuperAdmin-only until
     * CLE-2892).
     */
    getBulkAppealLetter: builder.query<
      IBulkAppealLetter,
      {
        accountId: string;
        classification: string;
        payerId: number | null;
        // CLE-2822: the list's active filters, so the letter covers the same claims.
        filters?: IBulkAppealFilters;
      }
    >({
      query: ({ accountId, classification, payerId, filters }) => ({
        url: `/claims/bulk-appeal-letter`,
        params: {
          accountId,
          classification,
          ...(payerId !== null ? { payerId } : {}),
          ...bulkAppealFilterParams(filters),
        },
      }),
    }),

    /**
     * CLE-2861: claims the payer adjudicated more than once — original vs final
     * allowed, the difference, and whether the underpayment was recovered — for
     * the Reprocessed Claims tab. Server-paginated and sorted; `summary` covers
     * every matching row. Any user with access to the account (SuperAdmin-only
     * until CLE-2892). Tagged "Claims" so a reprocess or a
     * payer change refreshes it.
     */
    getReprocessedClaims: builder.query<
      IReprocessedClaimsResponse,
      { accountId: string; filters: IReprocessedClaimsFilters }
    >({
      query: ({ accountId, filters }) => ({
        url: "/claims/reprocessed",
        params: { accountId, ...reprocessedClaimsParams(filters) },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * CLE-2861: the options for the Reprocessed Claims page's Original
     * Classification, Taxonomy and Rendering Provider filters, computed over
     * the account's whole page population with no filters applied. Each array
     * arrives sorted by the server. SuperAdmin-only.
     */
    getReprocessedClaimsFilterOptions: builder.query<
      IReprocessedClaimsFilterOptions,
      { accountId: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/reprocessed/filter-options",
        params: { accountId },
      }),
      providesTags: ["Claims"],
    }),

    /**
     * CLE-2861: "Rescan claims" — re-derives the Reprocessed Claims rows of
     * existing episodes from the remits already on file (no claim amounts
     * change). Responds 202 with the number of claims it will check and runs
     * in the background, so the page only fills in on a later refetch; 409
     * while a rescan is already running. Omit `accountId` to scan every
     * account. SuperAdmin-only.
     * No UI calls it since the initial prod scan; kept for a rescan after a rule change.
     */
    rescanReprocessedClaims: builder.mutation<
      IReprocessedClaimsRescanResponse,
      { accountId?: string }
    >({
      query: ({ accountId }) => ({
        url: "/claims/reprocessed/backfill",
        method: "POST",
        body: accountId ? { accountId } : {},
      }),
      invalidatesTags: ["Claims"],
    }),

    /**
     * CLE-2702: the bulk-appeal email templates. A SuperAdmin gets every template
     * in full; since CLE-2892 a HospitalStaff user gets only `{ classification }`
     * of the ACTIVE templates (all the Bulk Appeals tab reads) — so outside the
     * SuperAdmin template editor, read nothing but `classification`.
     */
    getAppealTemplates: builder.query<IAppealTemplate[], void>({
      query: () => ({
        url: `/appeal-templates`,
      }),
      providesTags: ["AppealTemplates"],
    }),

    /**
     * CLE-2702: create or update (by classification) a bulk-appeal email
     * template. SuperAdmin-only.
     */
    upsertAppealTemplate: builder.mutation<
      IAppealTemplate,
      { classification: string; subject: string; body: string }
    >({
      query: (body) => ({
        url: `/appeal-templates`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AppealTemplates"],
    }),
  }),
});

export const {
  useGetClaimsQuery,
  useLazyExportClaimsQuery,
  useAnalyzeClaimMutation,
  useAnalyzeDifferencesMutation,
  useUpdateResolutionStatusMutation,
  useGetActivePromptQuery,
  useCreatePromptVersionMutation,
  useGetAllPromptVersionsQuery,
  useGetDistinctClassificationsQuery,
  useGetDistinctDenialClassificationsQuery,
  useGetDistinctClearPayersQuery,
  useGetDistinctEdiFilesQuery,
  useGetSimilarCorrectClaimsQuery,
  useReprocessClaimMutation,
  useGetClaimByIdQuery,
  useCheckNcciBypassAvailableQuery,
  useToggleNcciBypassMutation,
  useChangeClaimPayerMutation,
  useChangeClaimProviderMutation,
  useBulkChangeClaimPayerMutation,
  useBulkUpdateResolutionStatusMutation,
  useBulkReprocessClaimMutation,
  useBulkAnalyzeClaimsMutation,
  useGetBulkAppealOpportunitiesQuery,
  useLazyGetBulkAppealLetterQuery,
  useGetReprocessedClaimsQuery,
  useGetReprocessedClaimsFilterOptionsQuery,
  useRescanReprocessedClaimsMutation,
  useGetAppealTemplatesQuery,
  useUpsertAppealTemplateMutation,
} = claimQueries;
